import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import FormGenerality from './generality';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import ChooseDate from 'shell/components/shared/choice-date-without-views-date-picker';
import { FormikErrors, FormikTouched } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DateTime } from 'luxon';
import { AccordionCivilUnion } from 'shell/components/shared/sipo-person-form/new-person';
import FormSearchPersonNewPerson from 'shell/components/shared/search-person-new-person';
import { transformCivilUnion } from 'shell/utils/consult-person/response-transformer/marital-status/civil-union';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { PersonalDataSearchVM } from 'shell/view-models/personal-data';
import PmiDivider from 'shell/components/shared/divider';

interface FormCivilUnionProps {
  values?: AccordionCivilUnion;
  errors?: FormikErrors<AccordionCivilUnion>;
  touched?: FormikTouched<AccordionCivilUnion>;
  onChange: (
    key: keyof AccordionCivilUnion,
    value: AccordionCivilUnion[keyof AccordionCivilUnion]
  ) => void;
  onFieldsChange: (values: AccordionCivilUnion) => void;
}

const FormCivilUnionComponent = ({
  values,
  onChange,
  onFieldsChange,
  errors,
  touched,
}: FormCivilUnionProps): React.ReactElement => {
  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values?.italian || {}),
        [key]: value,
      });
    },
    [values?.italian, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values?.foreign || {}),
        [key]: value,
      });
    },
    [values?.foreign, onChange]
  );

  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('municipality', municipality);
    }
  );

  const handleForeignStateListChange = useEventCallback(
    (citizenship: ForeignStateVM | null | undefined) => {
      onChange('citizenship', citizenship);
    }
  );

  const onFieldsGeneralityChange = useCallback(
    (vals) => {
      onChange('generality', {
        ...(values?.generality || {}),
        ...vals,
      });
    },
    [values?.generality, onChange]
  );

  const onChooseDateChange = useEventCallback((value: DateTime | null) => {
    onFieldsChange({
      eventDate: value,
    });
  });

  const onCivilUnionFieldsChange = useEventCallback((fields: PersonalDataSearchVM) => {
    const civilUnionData = transformCivilUnion(fields.subjectData || {});

    const person = transformPerson(fields.subjectData || {});

    onFieldsChange({
      ...(civilUnionData || {}),
      person,
      ...fields,
    });
  });

  return (
    <Grid container spacing={2}>
      <FormSearchPersonNewPerson
        onChangeFields={onCivilUnionFieldsChange}
        values={values}
        onChange={onChange}
      />
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Unito civilmente</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormGenerality
          generality={values?.generality}
          onChangeGenerality={onFieldGeneralityChange}
          labelMunicipalityItalian="Comune di nascita"
          labelProvinceItalian="Provincia di nascita"
          onChangeFieldsGenerality={onFieldsGeneralityChange}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Cittadinanza</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={4}>
        <ForeignStateList
          label="Cittadinanza"
          aria-label="Cittadinanza"
          value={values?.citizenship || null}
          onChange={handleForeignStateListChange}
          error={touched?.citizenship && Boolean(errors?.citizenship)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Valido dal"
          aria-label="Valido dal"
          value={values?.from || null}
          onChange={(date) => {
            onChange('from', date);
          }}
          renderInput={(parameters) => (
            <PmiTextField
              {...parameters}
              id="from"
              fullWidth
              error={touched?.from && Boolean(errors?.from)}
              helperText={errors?.from}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note"
          aria-label="Note"
          id="note"
          fullWidth
          multiline
          rows={2}
          inputProps={{
            maxLength: '240',
          }}
          value={values?.note || ''}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
          onChange={(event) => {
            onChange('note', event?.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography component="h6" variant="h6">
          Evento
        </Typography>
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          selected={values?.eventDate || null}
          error={touched?.eventDate && Boolean(errors?.eventDate)}
          onChange={onChooseDateChange}
          required
          label="Data evento"
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Comune</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Evento"
            id="isForeign"
            defaultValue={false}
            value={values?.isForeign || 'false'}
            onChange={() => {
              onFieldsChange({
                ...values,
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
              return;
            }}
          >
            <FormControlLabel value="false" control={<Radio />} label="Evento Italia" />
            <FormControlLabel value="true" control={<Radio />} label="Evento Stato Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          values={values?.italian}
          onChange={onFieldItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality="Comune evento"
          labelProvince="Provincia evento"
          eventDate={values?.eventDate}
        />
      ) : (
        <Foreign
          values={values?.foreign}
          onChange={onFieldForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          labelMunicipality="Comune evento"
          labelState="Stato evento"
        />
      )}
      <Grid item xs={12}>
        <PmiTextField
          label="Luogo eccezionale"
          aria-label="Luogo eccezionale"
          id="exceptional-location"
          fullWidth
          inputProps={{
            maxLength: '120',
          }}
          value={values?.exceptionalLocation || ''}
          error={touched?.exceptionalLocation && Boolean(errors?.exceptionalLocation)}
          helperText={errors?.exceptionalLocation}
          onChange={(event) => {
            onChange('exceptionalLocation', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Atto</PmiTitleDivider>
      </Grid>
      <Grid item container spacing={2} xs={12} alignItems="center">
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data atto"
            aria-label="Data atto"
            value={values?.documentDate || null}
            onChange={(date) => {
              onChange('documentDate', date);
            }}
            renderInput={(parameters) => (
              <PmiTextField
                {...parameters}
                id="documentDate"
                fullWidth
                error={touched?.documentDate && Boolean(errors?.documentDate)}
                helperText={errors?.documentDate}
              />
            )}
          />
        </Grid>
        <Grid item xs>
          <FormControlLabel
            control={
              <Checkbox
                id="documentTranscript"
                checked={values?.transcript || false}
                onChange={() => onChange('transcript', !values?.transcript)}
                inputProps={{ 'aria-label': 'Atto trascritto' }}
              />
            }
            label="Atto trascritto"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={9}>
        <MunicipalityList
          label="Comune"
          aria-label="Comune"
          value={values?.municipality || null}
          error={touched?.municipality && Boolean(errors?.municipality)}
          onChange={handleMunicipalityListChange}
          fromDate={values?.documentDate}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Provincia"
          id="province"
          inputProps={{
            maxLength: '2',
          }}
          aria-label="Provincia"
          fullWidth
          value={values?.municipality?.province || ''}
          onChange={(event) => {
            onChange('province', event.target.value);
          }}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Anno"
          aria-label="Anno"
          id="year"
          fullWidth
          inputProps={{
            maxLength: '4',
          }}
          value={values?.year || ''}
          error={touched?.year && Boolean(errors?.year)}
          helperText={errors?.year}
          onChange={(event) => {
            onChange('year', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Nr.Atto"
          aria-label="Nr.Atto"
          id="document-number"
          fullWidth
          inputProps={{
            maxLength: '20',
          }}
          value={values?.documentNumber || ''}
          error={touched?.documentNumber && Boolean(errors?.documentNumber)}
          helperText={errors?.documentNumber}
          onChange={(event) => {
            onChange('documentNumber', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Parte"
          aria-label="Parte"
          id="portion"
          fullWidth
          inputProps={{
            maxLength: '5',
          }}
          value={values?.portion || ''}
          error={touched?.portion && Boolean(errors?.portion)}
          helperText={errors?.portion}
          onChange={(event) => {
            onChange('portion', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Serie"
          aria-label="Serie"
          id="range"
          fullWidth
          inputProps={{
            maxLength: '10',
          }}
          value={values?.range || ''}
          error={touched?.range && Boolean(errors?.range)}
          helperText={errors?.range}
          onChange={(event) => {
            onChange('range', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Reg/Vol"
          aria-label="Reg/Vol"
          id="reg-vol"
          fullWidth
          value={values?.regVol || ''}
          inputProps={{
            maxLength: '5',
          }}
          error={touched?.regVol && Boolean(errors?.regVol)}
          helperText={errors?.regVol}
          onChange={(event) => {
            onChange('regVol', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormCivilUnionComponent.displayName = 'Form civil union component';

const FormCivilUnion = memo(FormCivilUnionComponent);

export default FormCivilUnion;
